// src/pages/admin/OrgInviteForm.jsx
import { useMemo, useState } from "react";
import { X, AlertCircle, Plus } from "lucide-react";
import { fetchWithAuth } from "../../api/fetchWithAuth";
import { useProjectStore } from "../../stores/useProjectStore";

import {
  ORG_ROLE_OPTIONS,
  DEFAULT_ORG_INVITE_ROLE,
} from "../../constants/orgRoles";
import {
  PROJECT_ROLE_OPTIONS,
  DEFAULT_PROJECT_INVITE_ROLE,
} from "../../constants/projectRoles";
import {
  LICENSE_TIER_OPTIONS,
  DEFAULT_LICENSE_TIER,
} from "../../constants/licenseTiers";

/**
 * The invite form half of the org-admin invite UX. Split out of the former
 * OrgInvitesPanel so it can be pinned while the pending-invite list scrolls
 * with the rest of the page; see OrgPendingInvites for the other half.
 *
 * Props:
 *   license   — /api/v1/org/license payload; drives the seat preview
 *   invites   — current invite list, needed because pending invites claim a
 *               seat server-side but are NOT in license.seats_used
 *   onInvited — called after a successful create so the parent can refresh
 *               invites + users + license counts together
 *
 * The form optionally stages project memberships. If the org has projects,
 * the "Add to projects" section appears expanded by default. Admin can add
 * rows, each row being one {project_id, role} pair that matches the backend's
 * project_memberships shape exactly.
 */
export default function OrgInviteForm({ license, invites = [], onInvited }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(DEFAULT_ORG_INVITE_ROLE);
  const [tier, setTier] = useState(DEFAULT_LICENSE_TIER);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  // Staged project-membership rows. Shape: [{ project_id, role }].
  // Mirrors the backend body format so we can JSON.stringify directly.
  const [stagedProjects, setStagedProjects] = useState([]);

  // Project list for the picker. Pulled from useProjectStore — already
  // hydrated at app load via hydrateProjects(). For org admins this
  // returns all org projects (see /api/v1/projects/available backend
  // branch), which is what we want here.
  const projects = useProjectStore((s) => s.projects);

  // Seat preview. Mirrors validateTierAvailability in admin-api
  // routes/projects.mjs, which is the authority — this only saves the admin
  // from filling out a form that is going to be rejected.
  //
  // Two details the server's math depends on, both easy to get wrong:
  //   - Claimed seats are active assignments PLUS pending invites. The
  //     license payload's seats_used counts assignments only, so pending
  //     invites have to be added here or a tier that is full purely by
  //     outstanding invites would look available.
  //   - /api/v1/org/license omits tiers the org has NEVER purchased and holds
  //     no assignments for. The server rejects those as "no purchased seats",
  //     so an absent tier means full, not unknown.
  //
  // A null license means the fetch failed, not that seats are gone: stay
  // enabled and let the server decide rather than blocking on missing data.
  //
  // ── THE TERM CHECK. C7, 2026-08-31, AND IT SHIPS WITH THE FILTER CHANGE ────
  //
  // `row.seats_unlimited` used to return blocked:false on its own. That was
  // survivable only by accident: a lapsed tier was FILTERED OUT of the payload
  // upstream, so it hit `!row` above and was blocked for being absent. The
  // server-side fix makes lapsed tiers appear — which means the filter change
  // alone would make this form LESS restrictive, opening an unlimited tier the
  // moment it stopped being hidden. Absence was the only thing blocking it.
  //
  // So the two changes are one change. This must not be split from the payload
  // fix in a later commit, and it must not be reverted on its own.
  //
  // Keyed on `assignable` — the server's own boolean, straight out of
  // org_holds_tier with zero grace — NOT on term_state, and not on dates. The
  // rule has one home (migration 061) and this is a render of its answer.
  // access_held is deliberately not consulted: an existing holder keeping
  // access during grace has no bearing on whether a NEW seat may be issued.
  const seatStatus = useMemo(() => {
    if (!license) return { blocked: false };

    const row = (license.tiers ?? []).find((t) => t.tier_code === tier);
    if (!row) return { blocked: true, reason: "This plan has no seats for that tier." };

    if (row.assignable === false) {
      const label = row.display_name ?? tier;
      if (row.term_state === "grace") {
        return { blocked: true, reason:
          `${label}'s license term has expired and is in its grace period. Existing users keep access; new seat assignments are paused until the term is renewed.` };
      }
      if (row.term_state === "pending") {
        return { blocked: true, reason:
          `${label}'s license term has not started yet. Seats can be assigned once it begins.` };
      }
      // "none" is a tier the org still has ASSIGNMENTS against but no active
      // purchase row — it appears in the payload for that reason alone. Saying
      // "lapsed" here would name a term that never existed to lapse.
      if (row.term_state === "none") {
        return { blocked: true, reason:
          `${label} has no active license. Seats cannot be assigned.` };
      }
      return { blocked: true, reason:
        `${label}'s license term has lapsed. Renew it to assign seats.` };
    }

    if (row.seats_unlimited) return { blocked: false };

    const purchased = Number(row.seats_total) || 0;
    const pending = invites.filter(
      (i) => i.tier_code === tier && !i.accepted_at && !i.revoked_at
    ).length;
    const claimed = (Number(row.seats_used) || 0) + pending;

    return claimed >= purchased
      ? {
          blocked: true,
          reason: `No seats left on this tier — ${claimed} of ${purchased} claimed`
            + (pending > 0 ? `, including ${pending} pending invite${pending === 1 ? "" : "s"}.` : "."),
        }
      : { blocked: false };
  }, [license, invites, tier]);

  const noSeatsLeft = seatStatus.blocked;

  // Projects not yet staged — drives the per-row dropdown options so
  // admins can't pick the same project twice. Computed once per render.
  const stagedProjectIds = useMemo(
    () => new Set(stagedProjects.map((s) => s.project_id)),
    [stagedProjects]
  );

  const availableProjects = useMemo(
    () => projects.filter((p) => !stagedProjectIds.has(p.id)),
    [projects, stagedProjectIds]
  );

  const hasProjects = projects.length > 0;
  const canAddAnotherRow = availableProjects.length > 0;

  // ── Project-row management ────────────────────────────────────────

  function addProjectRow() {
    const first = availableProjects[0];
    if (!first) return;
    setStagedProjects((rows) => [
      ...rows,
      { project_id: first.id, role: DEFAULT_PROJECT_INVITE_ROLE },
    ]);
  }

  function updateProjectRow(index, patch) {
    setStagedProjects((rows) =>
      rows.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  }

  function removeProjectRow(index) {
    setStagedProjects((rows) => rows.filter((_, i) => i !== index));
  }

  // ── Send ──────────────────────────────────────────────────────────

  async function sendInvite() {
    const trimmed = email.trim();
    if (!trimmed) {
      setFormError("Enter an email address");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setFormError("That doesn't look like a valid email");
      return;
    }

    setFormError(null);
    setLoading(true);

    // Only send project_memberships if non-empty — keeps the payload
    // clean and the backend path un-invoked on the common case.
    const body = { email: trimmed.toLowerCase(), role, tier_code: tier };
    if (stagedProjects.length > 0) {
      body.project_memberships = stagedProjects;
    }

    try {
      const res = await fetchWithAuth(`/api/v1/admin/orgs/invites`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let msg = `Invite failed (${res.status})`;
        try {
          const b = await res.json();
          if (b?.error) msg = b.error;
        } catch { /* not JSON */ }
        setFormError(msg);
        return;
      }

      // Reset form including staged projects.
      setEmail("");
      setRole(DEFAULT_ORG_INVITE_ROLE);
      setTier(DEFAULT_LICENSE_TIER);
      setStagedProjects([]);
      notifyParent();
    } catch (e) {
      console.error("[invites] create error", e);
      setFormError("Network error. Try again.");
    } finally {
      setLoading(false);
    }
  }

  function notifyParent() {
    if (typeof onInvited === "function") {
      Promise.resolve(onInvited()).catch((err) =>
        console.error("[invites] onInvited callback failed", err)
      );
    }
  }

  const inviteDisabled = loading || noSeatsLeft;

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setFormError(null);
          }}
          placeholder="user@email.com"
          disabled={loading}
          className="flex-1 bg-bg border border-slate-700 rounded px-2 py-1 text-xs disabled:opacity-50"
        />

        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={loading}
          className="bg-bg border border-slate-700 rounded px-2 py-1 text-xs disabled:opacity-50"
        >
          {ORG_ROLE_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>

        <select
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          disabled={loading}
          className="bg-bg border border-slate-700 rounded px-2 py-1 text-xs disabled:opacity-50"
          title="License tier"
        >
          {LICENSE_TIER_OPTIONS.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        <button
          disabled={inviteDisabled}
          onClick={sendInvite}
          className="px-3 py-1 rounded bg-accent-action/30 text-xs hover:bg-accent-action/50
                     disabled:opacity-40 disabled:cursor-not-allowed"
          title={noSeatsLeft ? seatStatus.reason : undefined}
        >
          Invite
        </button>
      </div>

      {/* Project memberships — only visible when the org has projects.
          Expanded by default so admins discover the capability. */}
      {hasProjects && (
        <div className="border border-slate-800 rounded px-2 py-2 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-wide text-fg-secondary">
              Add to projects
              {stagedProjects.length > 0 && (
                <span className="ml-1.5 text-gray-400 normal-case tracking-normal">
                  ({stagedProjects.length})
                </span>
              )}
            </span>
            <button
              onClick={addProjectRow}
              disabled={loading || !canAddAnotherRow}
              className="flex items-center gap-1 text-[10px] text-accent-action
                         hover:text-accent-action transition
                         disabled:opacity-40 disabled:cursor-not-allowed"
              title={
                !canAddAnotherRow
                  ? "All projects already staged"
                  : "Add another project"
              }
            >
              <Plus size={11} />
              Add project
            </button>
          </div>

          {stagedProjects.length === 0 && (
            <div className="text-[10px] text-fg-secondary py-1">
              Optional. Leave blank and the user joins the org only.
            </div>
          )}

          {stagedProjects.map((row, i) => {
            // Selected project must be in the dropdown even though it's
            // filtered out of availableProjects — otherwise selecting it
            // would make it disappear. Show the currently-selected one +
            // anything not yet staged.
            const optionsForThisRow = projects.filter(
              (p) => p.id === row.project_id || !stagedProjectIds.has(p.id)
            );

            return (
              <div key={i} className="flex gap-2 items-center">
                <select
                  value={row.project_id}
                  onChange={(e) =>
                    updateProjectRow(i, { project_id: e.target.value })
                  }
                  disabled={loading}
                  className="flex-1 bg-bg border border-slate-700 rounded px-2 py-1 text-xs disabled:opacity-50"
                >
                  {optionsForThisRow.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>

                <select
                  value={row.role}
                  onChange={(e) => updateProjectRow(i, { role: e.target.value })}
                  disabled={loading}
                  className="bg-bg border border-slate-700 rounded px-2 py-1 text-xs disabled:opacity-50"
                >
                  {PROJECT_ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => removeProjectRow(i)}
                  disabled={loading}
                  className="text-fg-secondary hover:text-red-400 transition disabled:opacity-40"
                  title="Remove"
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Form-level error (validation or backend rejection) */}
      {formError && (
        <div className="flex items-center gap-1.5 text-xs text-red-400">
          <AlertCircle size={12} />
          {formError}
        </div>
      )}

      {/* Seat-cap nudge — surfaces *why* the button is disabled */}
      {noSeatsLeft && !formError && (
        <div className="text-xs text-amber-400">
          {seatStatus.reason} Revoke a pending invite, free a seat by disabling
          a user, or pick a different tier.
        </div>
      )}
    </div>
  );
}
